import { useEffect, useMemo, useRef, useState, type ElementRef, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudIcon, type MudTextInput } from '@egov-moldova/mud-react';
import { DialogCombobox, formatExpiry, formatNumber, MetadataLine } from '@simsm/ui-shared';
import {
  searchCorrectableLots,
  type CorrectableLot,
  type EligibleMedicine,
} from '../../../features/corrections/api';
import '../reporting/stock-movement-dialog.css';

/** A lot together with the medicine it belongs to — what a pick actually is. */
export interface PickedLot {
  medicine: EligibleMedicine;
  lot: CorrectableLot;
}

/**
 * The identity of a medicine as every other correction screen prints it: name,
 * form and dose, falling back to the nomenclator code when the catalogue no
 * longer knows it. A code the reader can look up beats a name the system
 * invented.
 */
export function medicineIdentity(m: {
  name: string;
  pharmaceuticalForm: string;
  dose: string;
  code: string;
}): string {
  const parts = [m.name, m.pharmaceuticalForm, m.dose].filter((part) => part.length > 0);
  return parts.length > 0 ? parts.join(' · ') : m.code;
}

/**
 * The option order the listbox renders: each medicine's lots, medicine by
 * medicine. `DialogCombobox` walks its groups in exactly this order, and
 * keyboard focus moves by index over it.
 */
export function flattenLots(medicines: EligibleMedicine[]): PickedLot[] {
  return medicines.flatMap((medicine) => medicine.lots.map((lot) => ({ medicine, lot })));
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

interface Props {
  onPick: (picked: PickedLot) => void;
  /**
   * Puts the cursor in the search as the dialog opens, as the wireframe's does
   * (`inp.current?.focus()`), which also opens the list of recent lots.
   */
  autoFocus?: boolean;
}

// About a sixth of a second of frames: the modal places its own first focus as
// it opens, and the search takes it from there.
const FOCUS_FRAMES = 10;

/**
 * caiet UC51-02 — "găsirea medicamentului și a lotului". The search runs over
 * the lots the requester still holds (UC51-03), not over the nomenclator: a
 * medicine that was never reported has no lot to correct, and offering it would
 * only produce a refusal one step later.
 *
 * Drawn as the wireframe draws it (SolicitariCorectare.tsx `CautaLot`) and as
 * the reporting dialog already does it: MUD's search field, and under it a menu
 * in the dialog body — each medicine headed by its identity (name, then Formă ·
 * Doză · Cod · DCI), its correctable lots beneath. An empty box already lists
 * the most recently reported eligible lots, because the reader who arrived here
 * often has one lot in mind and no word to describe it with.
 */
export function CorrectableLotCombobox({ onPick, autoFocus = false }: Props): JSX.Element {
  const { t } = useTranslate();
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 200);
  const fieldRef = useRef<ElementRef<typeof MudTextInput> | null>(null);

  // AGE's field takes the focus on its inner input, which is in its shadow root.
  useEffect(() => {
    if (!autoFocus) return undefined;
    let frames = 0;
    let id = 0;
    const tick = (): void => {
      const host = fieldRef.current as HTMLElement | null;
      const inner = host?.shadowRoot?.querySelector<HTMLElement>('input');
      if (inner && document.activeElement !== host) inner.focus();
      frames += 1;
      if (frames < FOCUS_FRAMES) id = requestAnimationFrame(tick);
    };
    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [autoFocus]);

  // A one-character query is dropped rather than sent: the server treats
  // anything under two characters as no medicine search at all, so sending it
  // would return the unfiltered list under a cache key that claims otherwise.
  const term = debounced.trim().length >= 2 ? debounced.trim() : '';
  const search = useQuery({
    queryKey: ['correctable-lots', term],
    queryFn: () => searchCorrectableLots(term),
    staleTime: 30_000,
  });

  const medicines = useMemo(() => search.data?.medicines ?? [], [search.data]);
  const windowDays = search.data?.correctionWindowDays ?? 0;

  const lotMeta = (lot: CorrectableLot): string =>
    t('correction_new.search.lot_meta', {
      quantity: formatNumber(Number(lot.quantity)),
      expiry: formatExpiry(lot.lotExpiry, lot.lotExpiryPrecision),
    });

  const groups = useMemo(
    () =>
      medicines.map((medicine) => ({
        key: medicine.code,
        // listbox > group > option: the medicine names the group, so it stays
        // out of every one of its lots' accessible names.
        label: medicineIdentity(medicine),
        heading: (
          <span className="simsm-movement-hit__main">
            <span className="simsm-movement-hit__title">{medicine.name || medicine.code}</span>
            <MetadataLine
              wrap
              items={[
                { label: t('operator_corrections.field.form'), value: medicine.pharmaceuticalForm },
                { label: t('operator_corrections.field.dose'), value: medicine.dose },
                { label: t('operator_corrections.field.code'), value: medicine.code },
                { label: t('operator_corrections.field.dci'), value: medicine.dci },
              ]}
            />
          </span>
        ),
        options: medicine.lots.map((lot) => ({ medicine, lot })),
      })),
    [medicines, t],
  );

  // A search that failed is not a search that found nothing.
  let status: ReactNode = null;
  if (search.isFetching) status = t('correction_new.search.loading');
  else if (search.isError) status = t('common.load_error');
  else if (medicines.length === 0) status = t('correction_new.search.empty', { days: windowDays });

  return (
    <DialogCombobox<PickedLot>
      className="simsm-correction-new__search"
      field="search"
      size="md"
      loading={search.isFetching}
      value={query}
      onValueChange={setQuery}
      groups={groups}
      optionKey={(option) => `${option.medicine.code}-${option.lot.lot}`}
      renderOption={(option) => (
        <span className="simsm-correction-lot">
          <span className="simsm-correction-lot__content">
            <span className="simsm-correction-lot__number">{option.lot.lot}</span>
            <span className="simsm-correction-lot__meta">{lotMeta(option.lot)}</span>
          </span>
          <MudIcon
            className="simsm-correction-lot__arrow"
            name="chevron-right"
            size={16}
            aria-hidden="true"
          />
        </span>
      )}
      optionLabel={(option) => `${option.lot.lot}, ${lotMeta(option.lot)}`}
      onSelect={onPick}
      listboxLabel={t('correction_new.search.results_aria_label')}
      status={status}
      ariaLabel={t('correction_new.search.aria_label')}
      placeholder={t('correction_new.search.placeholder')}
      required
      inputRef={fieldRef}
    />
  );
}
